import React, { useMemo } from 'react';
import { Alert, SelectPopover } from '@planne-software/uni';
import { IconAlertCircle, IconCheck } from '@planne-software/uni/icons';
import { Button } from '@planne-software/uni/Button';
import { Typography } from '@planne-software/uni/mui/material';
import UniTheme from '@src/common/ui/UniTheme';
import { useAppSelector, useTranslate } from '@src/core/hooks';
import useBreakpoint from '@src/hooks/useBreakpoint';
import { useGuardedSequentialOpenChange } from '../../hooks/useGuardedSequentialOpenChange';
import { useTariffSelector } from '../../hooks/useTariffSelector';
import { POPOVER_WIDTH } from './constants';
import { ContentBox, EmptyMessage, FooterColumn, TotalLabel, TotalRow, TotalValue } from './styles';
import TariffCard from './TariffCard';
import TariffBinVerificationNote from './TariffBinVerificationNote';
import TariffPriceLine from './TariffPriceLine';
import TariffQuantityStepper from './TariffQuantityStepper';
import TariffServiceFeeNote from './TariffServiceFeeNote';
import VerificationFields from './VerificationFields';
import { SequentialSlotShell } from '../SequentialSlotShell';
import {
  currentProductAdderStatus,
  hasRepricingErrorSelector,
  isRepricingSelector,
  repricingByTariffIdSelector,
} from '@src/stores/product';
import { getSelectedTariff, useTariffQuantityRepricing } from '@src/domains/tariffs';
import { useWidgetId } from '@src/core/IdProvider';
import useSelectedProductId from '@src/hooks/useSelectedProductId';

const TariffSelectorContent = () => {
  const { i18n } = useTranslate();
  const { isMobile } = useBreakpoint();
  const {
    placeholder,
    selectedSummary,
    disabled,
    open,
    onOpenChange,
    tariffs,
    status,
    onUpdateQuantity,
    onConfirm,
    acceptableQuantitiesFor,
    priceInfoFor,
    descriptionFor,
    isDependent,
    hasServiceFeeIn,
    calculateTariffIndex,
    packageDiscount,
    loadingVerifications,
    verificationsValid,
    totalLabel,
    showSingleError,
    showMultipleError,
    showIndependentError,
    showMaxAvailabilityError,
    hasAnyError,
    selectedHour,
  } = useTariffSelector();

  const widgetId = useWidgetId();
  const productId = useSelectedProductId();
  useTariffQuantityRepricing(widgetId, productId);
  const repricingByTariffId = useAppSelector(repricingByTariffIdSelector(widgetId));
  const isRepricing = useAppSelector(isRepricingSelector(widgetId));
  const hasRepricingError = useAppSelector(hasRepricingErrorSelector(widgetId));
  const guardedOnOpenChange = useGuardedSequentialOpenChange('tariff', onOpenChange);
  const currentStatus = useAppSelector(currentProductAdderStatus(widgetId));
  const isAddingToCart = useMemo(() => currentStatus === 'adding_to_cart', [currentStatus]);

  const selectedElement = useMemo(() => {
    if (!selectedSummary) {
      return undefined;
    }

    return (
      <Typography variant='text-sm-semibold' noWrap sx={{ textAlign: 'left', width: '100%' }}>
        {selectedSummary}
      </Typography>
    );
  }, [selectedSummary]);

  const disabledConfirmButton = useMemo(
    () =>
      selectedSummary == null ||
      loadingVerifications ||
      !verificationsValid ||
      hasAnyError ||
      isAddingToCart ||
      isRepricing,
    [selectedSummary, loadingVerifications, verificationsValid, hasAnyError, isAddingToCart, isRepricing],
  );

  const confirmButton = useMemo(
    () => (
      <Button
        fullWidth
        color='primary'
        variant='contained'
        startSlot={<IconCheck size={20} />}
        disabled={disabledConfirmButton}
        onClick={onConfirm}
      >
        {i18n('base_confirm')}
      </Button>
    ),
    [disabledConfirmButton, onConfirm, i18n],
  );

  const mobileFooter = (
    <FooterColumn>
      {totalLabel ? (
        <TotalRow>
          <TotalLabel>{i18n('cart_totalLabel')}</TotalLabel>
          <TotalValue>{isRepricing ? i18n('adder_updating_price') : totalLabel}</TotalValue>
        </TotalRow>
      ) : null}
      {confirmButton}
    </FooterColumn>
  );

  const errorBannerMessage = showMultipleError ? i18n('adder_multipleErrorLabel') : null;

  return (
    <SequentialSlotShell slot='tariff'>
      <SelectPopover
        placeholder={placeholder}
        selectedElement={selectedElement}
        bottomSheetTitle={placeholder}
        isBottomSheet
        fullWidth
        error={hasAnyError}
        open={open}
        onOpenChange={guardedOnOpenChange}
        popoverWidth={isMobile ? undefined : POPOVER_WIDTH}
        footer={isMobile ? mobileFooter : confirmButton}
        disablePopover={isAddingToCart}
        disabled={disabled || isAddingToCart}
      >
        <ContentBox isMobile={isMobile} aria-label='tariff-list'>
          {status === 'ready' && tariffs.some((t) => hasServiceFeeIn(t)) ? (
            <TariffServiceFeeNote label={i18n('product_booking_service_fee_note')} />
          ) : null}
          {status === 'empty' ? <EmptyMessage>{i18n('product_booking_no_tariffs')}</EmptyMessage> : null}
          {status === 'ready'
            ? tariffs.map((tariff, idx) => {
                const tariffId = getSelectedTariff(tariff, selectedHour?.minutes ?? null)?.id;
                const isRepricing = repricingByTariffId[tariffId ?? ''] === 'loading';
                const [min, max] = acceptableQuantitiesFor(tariff);
                return (
                  <React.Fragment key={tariff.type.id}>
                    <TariffCard
                      name={tariff.type.name}
                      isAvailable={tariff.isAvailable}
                      isDependent={isDependent(tariff.type.id)}
                      dependentLabel={i18n('adder_dependent_tariff')}
                      unavailableLabel={i18n('adder_unavailableTariffNotice')}
                      description={descriptionFor(tariff)}
                      hasError={showMultipleError}
                      layout='mobile'
                      priceLine={
                        <TariffPriceLine
                          priceFormatted={priceInfoFor(tariff)}
                          oldPriceFormatted={priceInfoFor(tariff, true)}
                          packageDiscount={packageDiscount}
                          priceFromLabel={i18n('adder_price_from')}
                          isRepricing={isRepricing}
                          updatingLabel={i18n('adder_updating_price')}
                        />
                      }
                      stepper={
                        <TariffQuantityStepper
                          quantity={tariff.selectedQuantity}
                          min={min}
                          max={max}
                          onChange={(next) => onUpdateQuantity(tariff.type.id, next)}
                          decrementAriaLabel={`decrement-${tariff.type.id}`}
                          incrementAriaLabel={`increment-${tariff.type.id}`}
                        />
                      }
                      noteSlot={<TariffBinVerificationNote tariffTypeId={tariff.type.id} />}
                      verificationFields={
                        <VerificationFields
                          tariffTypeId={tariff.type.id}
                          quantity={tariff.selectedQuantity}
                          tariffTypePosition={calculateTariffIndex(idx)}
                        />
                      }
                    />
                    {tariff.isAvailable && showSingleError(tariff.type.id) ? (
                      <Alert severity='error' title={i18n('adder_singleErrorLabel')} />
                    ) : null}
                    {showIndependentError(tariff.type.id) ? (
                      <Alert severity='warning' title={i18n('adder_dependent_tariff_selection_error')} />
                    ) : null}
                    {tariff.isAvailable && showMaxAvailabilityError(tariff) ? (
                      <Alert
                        startIcon={<IconAlertCircle size={20} />}
                        severity='error'
                        title={i18n('product_booking_tariff_max_availability_reached')}
                      />
                    ) : null}
                  </React.Fragment>
                );
              })
            : null}
          {status === 'ready' && errorBannerMessage ? <Alert severity='error' title={errorBannerMessage} /> : null}
          {status === 'ready' && hasRepricingError ? (
            <Alert severity='info' title={i18n('adder_price_update_failed')} />
          ) : null}
        </ContentBox>
      </SelectPopover>
    </SequentialSlotShell>
  );
};

const TariffSelector = () => (
  <UniTheme>
    <TariffSelectorContent />
  </UniTheme>
);

export default TariffSelector;
